import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { navSections } from './DesktopSidebar.config';
import { DesktopSidebarOrganization } from './DesktopSidebarOrganization';
import { SidebarSectionBlock } from './DesktopSidebarNav';
import type { SidebarGroup } from './DesktopSidebar.types';
import { useSidebarCollapsed } from './sidebarCollapse';
import { useNavFitScale } from './useNavFitScale';

const ORGANIZATION_TITLE = 'КОМПАНИЯ';

const FADE = '18px';

/** Затухание у краёв прокручиваемого списка: сверху — если уже прокрутили,
 *  снизу — если ниже есть пункты. Прокручивать нечего — маски нет. */
function edgeFade({ top, bottom }: { top: boolean; bottom: boolean }) {
  if (top && bottom) {
    return `linear-gradient(to bottom, transparent, #000 ${FADE}, #000 calc(100% - ${FADE}), transparent)`;
  }
  if (bottom) {
    return `linear-gradient(to bottom, #000 calc(100% - ${FADE}), transparent)`;
  }
  if (top) {
    return `linear-gradient(to bottom, transparent, #000 ${FADE})`;
  }
  return undefined;
}

type DesktopSidebarMenuProps = {
  groups: SidebarGroup[];
  groupsLoading: boolean;
  otdelsOpen: boolean;
  selectedGroupId: string | null;
  selectedGroupName: string;
  onNavigate: (to: string) => void;
  onOtdelsOpenChange: (open: boolean) => void;
};

export function DesktopSidebarMenu({
  groups,
  groupsLoading,
  otdelsOpen,
  selectedGroupId,
  selectedGroupName,
  onNavigate,
  onOtdelsOpenChange,
}: DesktopSidebarMenuProps) {
  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    navSections.forEach((s) => initial.add(s.title));
    initial.add(ORGANIZATION_TITLE);
    return initial;
  });

  const toggleSection = (title: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };

  const [workSection, ...restSections] = navSections;

  // Свернули или развернули сайдбар — у списка другая высота, масштаб заново.
  const collapsed = useSidebarCollapsed();
  const { navRef, contentRef } = useNavFitScale(collapsed);

  // Список на низком экране прокручивается (масштаб не ниже 0.8, см.
  // useNavFitScale) — активный пункт докручиваем в поле зрения, иначе не
  // видно, в каком ты разделе.
  const { pathname } = useLocation();
  useEffect(() => {
    const reveal = () => {
      const nav = navRef.current;
      const active = nav?.querySelector<HTMLElement>('[aria-current="page"]');
      if (!nav || !active) {
        return;
      }
      const box = nav.getBoundingClientRect();
      const item = active.getBoundingClientRect();
      if (item.top < box.top || item.bottom > box.bottom) {
        nav.scrollTop += item.top - box.top - (box.height - item.height) / 2;
      }
    };
    // Сразу после отрисовки — и ещё раз, когда масштаб устоится: он
    // подгоняется несколько кадров после монтирования и сдвигает пункты.
    const raf = requestAnimationFrame(reveal);
    const settle = window.setTimeout(reveal, 300);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(settle);
    };
  }, [collapsed, pathname, navRef]);

  // Полосы прокрутки нет — что список продолжается, говорит затухание у края.
  const [edges, setEdges] = useState({ top: false, bottom: false });
  useEffect(() => {
    const nav = navRef.current;
    const content = contentRef.current;
    if (!nav) {
      return;
    }
    const update = () => {
      const top = nav.scrollTop > 1;
      const bottom = nav.scrollTop + nav.clientHeight < nav.scrollHeight - 1;
      setEdges((prev) =>
        prev.top === top && prev.bottom === bottom ? prev : { top, bottom }
      );
    };
    update();
    nav.addEventListener('scroll', update, { passive: true });
    // Список меняет высоту сам: масштаб, аккордеоны, свёрнутый вид.
    const observer = new ResizeObserver(update);
    observer.observe(nav);
    if (content) {
      observer.observe(content);
    }
    return () => {
      nav.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [navRef, contentRef]);

  const fade = edgeFade(edges);

  // Прокручивается только список: шапка, кнопки и «Контроль» — вне <nav>.
  return (
    <nav
      ref={navRef}
      className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      style={fade ? { maskImage: fade, WebkitMaskImage: fade } : undefined}
    >
      <div
        ref={contentRef}
        className="flex flex-col gap-[calc(2px*var(--nav-scale,1))] px-3"
      >
        <SidebarSectionBlock
          section={workSection}
          open={openSections.has(workSection.title)}
          onToggle={() => toggleSection(workSection.title)}
        />

        <DesktopSidebarOrganization
          groups={groups}
          groupsLoading={groupsLoading}
          accordionOpen={openSections.has(ORGANIZATION_TITLE)}
          otdelsOpen={otdelsOpen}
          selectedGroupId={selectedGroupId}
          selectedGroupName={selectedGroupName}
          onNavigate={onNavigate}
          onAccordionToggle={() => toggleSection(ORGANIZATION_TITLE)}
          onOtdelsOpenChange={onOtdelsOpenChange}
        />

        {restSections.map((section) => (
          <SidebarSectionBlock
            key={section.title}
            section={section}
            open={openSections.has(section.title)}
            onToggle={() => toggleSection(section.title)}
            divider
          />
        ))}
      </div>
    </nav>
  );
}
